import { z } from 'zod';

/**
 * Interview Performance Analysis Types (Feature 002)
 * 
 * Multi-dimensional scoring system for completed interviews
 * 4 dimensions: Communication, Technical, Cultural Fit, Role Readiness
 */

// Score Details Schema (matches JSONB structure in database)
export const ScoreDetailsSchema = z.object({
  communication: z.object({
    explanation: z.string().min(50).max(200),
    examples: z.array(z.string()).min(2).max(3),
    fillerWordCount: z.number().optional(),
  }),
  technical: z.object({
    explanation: z.string().min(50).max(200),
    examples: z.array(z.string()).min(2).max(3),
    techStack: z.array(z.string()).optional(),
  }),
  cultural: z.object({
    explanation: z.string().min(50).max(200),
    examples: z.array(z.string()).min(2).max(3),
    redFlags: z.array(z.string()).optional(),
  }),
  readiness: z.object({
    explanation: z.string().min(50).max(200),
    examples: z.array(z.string()).min(2).max(3),
    gaps: z.array(z.string()).optional(),
  }),
});

export type ScoreDetails = z.infer<typeof ScoreDetailsSchema>;

// Interview Performance Analysis Record (database entity)
export interface InterviewPerformanceAnalysis {
  id: string;
  interviewId: string;
  communicationScore: number; // 0-100
  technicalScore: number; // 0-100
  culturalFitScore: number; // 0-100
  roleReadinessScore: number; // 0-100
  finalWeightedScore: number; // 0-100
  scoreDetails: ScoreDetails;
  summaryComment: string;
  recommendation: 'strongly_recommend' | 'recommend' | 'maybe' | 'not_recommended';
  generatedBy: string | null;
  modelVersion: string;
  processingTimeMs: number;
  createdAt: Date;
  updatedAt: Date;
}

// API Response Schema (what frontend receives)
export const InterviewAnalysisResponseSchema = z.object({
  id: z.string(),
  interviewId: z.string(),
  scores: z.object({
    communication: z.number().min(0).max(100),
    technical: z.number().min(0).max(100),
    culturalFit: z.number().min(0).max(100),
    roleReadiness: z.number().min(0).max(100),
    finalWeighted: z.number().min(0).max(100),
  }),
  details: ScoreDetailsSchema,
  summary: z.string().min(200).max(500),
  recommendation: z.enum(['strongly_recommend', 'recommend', 'maybe', 'not_recommended']),
  metadata: z.object({
    generatedAt: z.string(), // ISO timestamp
    modelVersion: z.string(),
    processingTimeMs: z.number(),
  }),
});

export type InterviewAnalysisResponse = z.infer<typeof InterviewAnalysisResponseSchema>;

// API Request Schema for chat
export const InterviewChatRequestSchema = z.object({
  message: z.string().min(1).max(1000),
  history: z.array(z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string(),
  })).max(20).optional(),
});

export type InterviewChatRequest = z.infer<typeof InterviewChatRequestSchema>;

// API Response Schema for chat
export const InterviewChatResponseSchema = z.object({
  response: z.string(),
  timestamp: z.string(), // ISO timestamp
  references: z.array(z.object({
    timestamp: z.string(), // HH:MM:SS
    text: z.string(),
  })).optional(),
});

export type InterviewChatResponse = z.infer<typeof InterviewChatResponseSchema>;

/**
 * Scoring Weights (as per Feature 002 spec FR-1)
 */
export const SCORING_WEIGHTS = {
  communication: 0.25,  // 25%
  technical: 0.30,      // 30% (highest weight)
  culturalFit: 0.25,    // 25%
  roleReadiness: 0.20,  // 20%
} as const;

/**
 * Calculate weighted final score
 */
export function calculateWeightedScore(scores: {
  communication: number;
  technical: number;
  culturalFit: number;
  roleReadiness: number;
}): number {
  return Math.round(
    scores.communication * SCORING_WEIGHTS.communication +
    scores.technical * SCORING_WEIGHTS.technical +
    scores.culturalFit * SCORING_WEIGHTS.culturalFit +
    scores.roleReadiness * SCORING_WEIGHTS.roleReadiness
  );
}

/**
 * Determine recommendation based on final score
 */
export function getRecommendation(
  finalScore: number
): 'strongly_recommend' | 'recommend' | 'maybe' | 'not_recommended' {
  if (finalScore >= 80) return 'strongly_recommend';
  if (finalScore >= 65) return 'recommend';
  if (finalScore >= 50) return 'maybe';
  return 'not_recommended';
}
